import { useEffect, useMemo, useRef, useState } from "react";

export const useLazyKeyedResource = ({
  keys,
  fetcher,
  enabled = true,
  onLoadingChange,
}) => {
  const [dataByKey, setDataByKey] = useState({});
  const fetchedKeysRef = useRef(new Set());
  const fetcherRef = useRef(fetcher);
  const onLoadingChangeRef = useRef(onLoadingChange);

  fetcherRef.current = fetcher;
  onLoadingChangeRef.current = onLoadingChange;

  const keysKey = Array.isArray(keys)
    ? [...new Set(keys.filter(Boolean))].sort().join("|")
    : "";

  const activeKeys = useMemo(
    () => (keysKey ? keysKey.split("|") : []),
    [keysKey],
  );

  useEffect(() => {
    if (!enabled) return undefined;

    const keysToFetch = activeKeys.filter(
      (key) => !fetchedKeysRef.current.has(key),
    );
    if (!keysToFetch.length) return undefined;

    let cancelled = false;
    onLoadingChangeRef.current?.(true);

    fetcherRef
      .current(keysToFetch)
      .then((resultMap) => {
        if (cancelled) return;
        keysToFetch.forEach((key) => fetchedKeysRef.current.add(key));
        setDataByKey((prev) => ({ ...prev, ...resultMap }));
      })
      .finally(() => {
        if (!cancelled) onLoadingChangeRef.current?.(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activeKeys, enabled]);

  return { dataByKey, activeKeys };
};
